import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withViewTransitions,
} from '@angular/router';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  CORE_API_CONFIG,
  CORE_ASSET_URL_CONFIG,
  CORE_AUTH_CONFIG,
  CORE_SESSION_CONFIG,
  CORE_PERMISSION_CONFIG,
  CORE_LOGGER_CONFIG,
  CORE_PERFORMANCE_CONFIG,
  CORE_SCREEN_PROTECTION_CONFIG,
  CORE_LOCALE_CONFIGS,
  CORE_LANGUAGE_NAMES,
  CORE_TRANSLOCO_SERVICE,
  DEFAULT_SCREEN_PROTECTION_CONSTANTS,
  ThemeService,
} from '@educational-initiatives/angular-web-utilities';
import { GlobalErrorHandler } from '@educational-initiatives/angular-web-utilities';
import { TranslocoService } from '@jsverse/transloco';

import { routes } from './app.routes';
import {
  authApiInterceptor,
  authInterceptor,
  credentialsInterceptor,
  errorInterceptor,
  heartbeatInterceptor,
  LANGUAGE_NAMES,
  loadingInterceptor,
} from '@app/core';
import { provideTranslocoConfig, registerLocales } from '@app/core';
import { LanguageService } from '@educational-initiatives/angular-web-utilities';
import type { AvailableLanguage } from '@app/core';
import {
  LOCALE_PATH_PATTERN,
  PERMISSION_VERSION,
  STORAGE_KEYS,
} from '@app/shared';
import {
  assertValidEnvironment,
  initializeTrustedTypes,
  loadAndApplyRuntimeConfig,
} from '@app/core';
import { SessionService } from '@educational-initiatives/angular-web-utilities';
import { provideLivekitClient } from '@educational-initiatives/ei-realtime-sdk';
import { environment } from '@env/environment';
import {
  APP_ID,
  COMMON_LOGIN_APP_ID,
  LOCALE_CONFIGS,
} from '@app/shared/constants';

/**
 * @fileoverview app.config.ts
 * @description
 * **The Application Configuration**
 * This file exports the root application configuration, including providers
 * for routing, HTTP client, internationalization, error handling, and
 * security features. It sets up the application's foundational services
 * and initializes critical security and i18n systems during bootstrap.
 *
 * ARCHITECTURAL ROLE:
 * Core / Configuration
 */
export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: CORE_API_CONFIG,
      useValue: {
        baseUrl: environment.apiUrl,
      },
    },
    {
      provide: CORE_ASSET_URL_CONFIG,
      useValue: {
        assetsBucketUrl: environment.assetsBucketUrl,
      },
    },
    {
      provide: CORE_AUTH_CONFIG,
      useValue: {
        defaultAppId: APP_ID,
        commonLoginAppId: COMMON_LOGIN_APP_ID,
        includeEnvInfo: true,
        env: environment.name,
      },
    },
    {
      provide: CORE_SESSION_CONFIG,
      useValue: {
        subAppId: APP_ID,
        useHeartbeat: true,
      },
    },
    {
      provide: CORE_PERMISSION_CONFIG,
      useValue: {
        appId: APP_ID,
        indexedDb: {
          DB: STORAGE_KEYS.PERMISSIONS_DB,
          STORE: STORAGE_KEYS.PERMISSIONS_STORE,
          VERSION: PERMISSION_VERSION,
        },
      },
    },
    {
      provide: CORE_LOGGER_CONFIG,
      useValue: {
        enableLog: environment.enableLog,
        logLevel: environment.logLevel,
        production: environment.production,
        logEndpoint: environment.logEndpoint,
        name: environment.name,
        version: environment.version,
      },
    },
    {
      provide: CORE_PERFORMANCE_CONFIG,
      useValue: {
        enablePerformanceMetrics: environment.features.enablePerformanceMetrics,
      },
    },
    {
      provide: CORE_SCREEN_PROTECTION_CONFIG,
      useValue: {
        production: environment.production,
        environmentName: environment.name,
        enableInProduction: true,
        enableInStaging: true,
        detectDevTools: false,
        constants: {
          ...DEFAULT_SCREEN_PROTECTION_CONSTANTS,
          PRINT_DIALOG_TIMEOUT_MS: 2500,
          PRINT_KEYBOARD_TIMEOUT_MS: 2500,
        },
      },
    },
    {
      provide: CORE_LOCALE_CONFIGS,
      useValue: LOCALE_CONFIGS,
    },
    {
      provide: CORE_LANGUAGE_NAMES,
      useValue: LANGUAGE_NAMES,
    },
    {
      provide: CORE_TRANSLOCO_SERVICE,
      useExisting: TranslocoService,
    },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideLivekitClient(),
    provideRouter(routes, withComponentInputBinding(), withViewTransitions()),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        credentialsInterceptor,
        authApiInterceptor,
        authInterceptor,
        loadingInterceptor,
        heartbeatInterceptor,
        errorInterceptor,
      ]),
    ),
    provideTranslocoConfig(),
    provideAppInitializer(async () => {
      // Must run before await AND before ThemeService constructs: empty
      // localStorage would otherwise default to 'system' (OS dark).
      if (
        typeof localStorage !== 'undefined' &&
        !localStorage.getItem('theme')
      ) {
        localStorage.setItem('theme', 'light');
      }
      inject(ThemeService);

      await loadAndApplyRuntimeConfig();
      assertValidEnvironment();
      initializeTrustedTypes();
      registerLocales();
    }),
    // Initialize language from base href (needed for signin and other routes outside MainLayout)
    provideAppInitializer(() => {
      if (typeof document === 'undefined') {
        return;
      }
      const languageService = inject(LanguageService);
      const baseHref =
        document.querySelector('base')?.getAttribute('href') || '/';
      const localeMatch = baseHref.match(LOCALE_PATH_PATTERN);
      if (localeMatch?.[1] && languageService.isValidLanguage(localeMatch[1])) {
        languageService.setLanguage(localeMatch[1] as AvailableLanguage);
      }
    }),
    // Session initialization - must run after HTTP client is available.
    // Skip on password-reset approval links: no session yet; 401 would otherwise
    // pair with errorInterceptor redirect before the reset flow runs.
    provideAppInitializer(() => {
      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        if (
          path.includes('/reset-password-request') ||
          path.includes('/reset-password')
        ) {
          return;
        }
      }
      const sessionService = inject(SessionService);
      return sessionService.createSessionActivity();
    }),
  ],
};
